import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import { brand } from 'themes/brand';

interface Sector {
  id: string;
  name: string;
}

interface SectorFilterProps {
  sectors: Sector[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  label?: string;
}

const SectorFilter: React.FC<SectorFilterProps> = ({
  sectors,
  selectedIds,
  onChange,
  label = 'Setor',
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');

  const open = Boolean(anchorEl);
  const hasSelection = selectedIds.length > 0;
  const isFloating = open || hasSelection;

  const triggerText =
    selectedIds.length === 0
      ? ''
      : selectedIds.length === 1
      ? '1 setor selecionado'
      : `${selectedIds.length} setores selecionados`;

  const filtered = useMemo(
    () =>
      sectors.filter((s) =>
        s.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [sectors, search],
  );

  const handleOpen = () => setAnchorEl(triggerRef.current);

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleToggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id],
    );
  };

  const handleRemove = (id: string) =>
    onChange(selectedIds.filter((s) => s !== id));

  const handleClear = () => onChange([]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Trigger — styled to match MUI outlined Select */}
      <Box
        ref={triggerRef}
        onClick={handleOpen}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          border: open
            ? `2px solid ${brand.primary}`
            : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          padding: open ? '15.5px 13px' : '16.5px 14px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          '&:hover': {
            borderColor: open ? brand.primary : 'rgba(0, 0, 0, 0.87)',
          },
        }}
      >
        {/* Floating label */}
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            left: isFloating ? '8px' : '14px',
            top: isFloating ? '-9px' : '50%',
            transform: isFloating ? 'none' : 'translateY(-50%)',
            fontSize: isFloating ? '12px' : '16px',
            color: open ? brand.primary : 'rgba(0, 0, 0, 0.6)',
            backgroundColor: '#fff',
            padding: '0 4px',
            transition: 'all 0.15s ease',
            pointerEvents: 'none',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            flex: 1,
            fontSize: '16px',
            color: hasSelection ? 'text.primary' : 'transparent',
            userSelect: 'none',
          }}
        >
          {triggerText || '​'}
        </Typography>

        <UnfoldMoreIcon
          sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '20px' }}
        />
      </Box>

      {/* Dropdown popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: anchorEl?.offsetWidth ?? 300,
            maxHeight: 360,
            display: 'flex',
            flexDirection: 'column',
            mt: 0.5,
            borderRadius: '4px',
          },
        }}
      >
        {/* Search input */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar setor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Sector list */}
        <List sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
          {filtered.map((sector) => {
            const isSelected = selectedIds.includes(sector.id);
            return (
              <ListItemButton
                key={sector.id}
                onClick={() => handleToggle(sector.id)}
                selected={isSelected}
                sx={{
                  py: 0.75,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    color: 'primary.main',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'primary.50',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {isSelected && <CheckIcon fontSize="small" color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={sector.name}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                Nenhum setor encontrado
              </Typography>
            </Box>
          )}
        </List>
      </Popover>

      {/* Selected chips */}
      {hasSelection && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1,
            alignItems: 'center',
          }}
        >
          {selectedIds.map((id) => {
            const sector = sectors.find((s) => s.id === id);
            if (!sector) return null;
            return (
              <Chip
                key={id}
                label={sector.name}
                onDelete={() => handleRemove(id)}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, borderRadius: '16px' }}
              />
            );
          })}
          <Button
            size="small"
            onClick={handleClear}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              minWidth: 'auto',
              px: 0.5,
            }}
          >
            Limpar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SectorFilter;
